import { Fragment, useId, type ReactElement, type ReactNode } from 'react';
import { Button, Skeleton, Tooltip } from '@/components/ui';
import { ChevronIcon, CloseIcon } from '@/components/catalog/icons';
import { cx } from '@/lib/cx';

/**
 * Product Insights presentation pieces: the funnel, ranked bar lists, conversion
 * pills, the active-filter chip row and the "Updated" fact. Pure presentation —
 * no fetching, no URL state; every figure arrives as a plain count.
 *
 * ⚠️ RATES ARE FRACTIONS. `ConversionPill.rate` is `numerator / denominator`
 * (0.34 renders "34%"), the same shape `stageRate()` in `use-fab-data.ts`
 * returns; `null` renders an em dash.
 *
 * ⚠️ BARS ARE GEOMETRY. Every bar is `aria-hidden`; the count and share beside
 * it are the text a screen reader gets. A non-zero value keeps a minimum width
 * so a 9 next to an 840 is still visible; a zero is an empty track.
 *
 * Colours are tokens only: `bg-chart-1` fills on a `bg-surface-sunken` track,
 * for both the funnel and BarList. Text never sits on a fill.
 */

// ── formatting (local so this kit does not depend on the data layer) ────────

const COUNT_FORMAT = new Intl.NumberFormat('en-GB', { maximumFractionDigits: 0 });

function countText(value: number | null | undefined): string {
  return value === null || value === undefined || !Number.isFinite(value)
    ? '—'
    : COUNT_FORMAT.format(value);
}

function percentText(fraction: number | null): string {
  if (fraction === null || !Number.isFinite(fraction)) return '—';
  const pct = Math.round(fraction * 100);
  if (pct === 0 && fraction > 0) return '<1%';
  return `${pct}%`;
}

/** Bar width in percent of the track. Zero stays zero; any positive value is at least `min`. */
function barWidth(value: number | null, peak: number, min: number): number {
  if (value === null || !Number.isFinite(value) || value <= 0 || peak <= 0) return 0;
  return Math.min(100, Math.max(min, (value / peak) * 100));
}

/** Refresh glyph (Lucide `refresh-cw`, same path data as `CycleIcon`), sized for an icon button. */
function RefreshIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={cx('h-4 w-4 shrink-0', className)}
    >
      <path d="M21 12a9 9 0 0 1-9 9 9 9 0 0 1-8.06-5" />
      <path d="M3 12a9 9 0 0 1 9-9 9 9 0 0 1 8.06 5" />
      <path d="M20 3v5h-5" />
      <path d="M4 21v-5h5" />
    </svg>
  );
}

/** Lucide `arrow-right` / `arrow-down`, decorative. */
function ArrowGlyph({ direction, className }: { direction: 'right' | 'down'; className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={cx('h-3 w-3 shrink-0', className)}
    >
      {direction === 'right' ? (
        <path d="M5 12h14M13 6l6 6-6 6" />
      ) : (
        <path d="M12 5v14M6 13l6 6 6-6" />
      )}
    </svg>
  );
}

// ── ConversionPill ───────────────────────────────────────────────────────────

/** "34% → admits". The arrow is decorative; assistive tech hears "34% to admits". */
export function ConversionPill({ rate, label }: { rate: number | null; label: string }): ReactElement {
  return (
    <span className="inline-flex h-6 max-w-full shrink-0 items-center gap-1 rounded-full border border-border bg-surface-sunken px-2 text-xs whitespace-nowrap text-fg-secondary">
      <span className="font-semibold text-fg tabular-nums">{percentText(rate)}</span>
      <ArrowGlyph direction="right" className="text-fg-subtle" />
      <span className="sr-only">to </span>
      <span className="truncate">{label}</span>
    </span>
  );
}

// ── FunnelChart ──────────────────────────────────────────────────────────────

export interface FunnelStage {
  key: string;
  label: string;
  value: number | null;
  onSelect?: () => void;
}

const FUNNEL_MIN_BAR_PCT = 3;

function FunnelStageBody({
  stage,
  peak,
  loading,
  interactive,
}: {
  stage: FunnelStage;
  peak: number;
  loading: boolean;
  interactive: boolean;
}) {
  const width = barWidth(stage.value, peak, FUNNEL_MIN_BAR_PCT);
  return (
    <>
      <span className="flex min-w-0 items-baseline justify-between gap-3 lg:flex-col lg:items-stretch lg:gap-1">
        <span className="flex min-w-0 items-center gap-1">
          <span className="truncate text-xs font-medium text-fg-muted" title={stage.label}>
            {stage.label}
          </span>
          {interactive && (
            <ChevronIcon
              direction="right"
              className="h-3.5 w-3.5 shrink-0 text-fg-subtle opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100 max-lg:opacity-100"
            />
          )}
        </span>
        {loading ? (
          <Skeleton className="h-6 w-16 shrink-0 lg:h-8 lg:w-20" />
        ) : (
          <span className="shrink-0 text-lg leading-7 font-semibold text-fg tabular-nums lg:text-2xl lg:leading-8">
            {countText(stage.value)}
          </span>
        )}
      </span>
      <span aria-hidden="true" className="mt-2 block h-2 overflow-hidden rounded-xs bg-surface-sunken">
        {!loading && (
          <span
            className="block h-full rounded-xs bg-chart-1 transition-[width] duration-300 motion-reduce:transition-none"
            style={{ width: `${width}%` }}
          />
        )}
      </span>
    </>
  );
}

/** The step between two stages: a down-arrow pill when stacked, a compact right-arrow rate when in a row. */
function FunnelConnector({
  rate,
  from,
  to,
  loading,
}: {
  rate: number | null;
  from: string;
  to: string;
  loading: boolean;
}) {
  if (loading) {
    return (
      <div className="flex items-center px-3 py-1 lg:justify-center lg:px-1 lg:py-0">
        <Skeleton className="h-6 w-20 rounded-full lg:w-12" />
      </div>
    );
  }
  return (
    <div className="flex items-center px-3 py-1 lg:justify-center lg:px-1 lg:py-0">
      {/* stacked (< lg) */}
      <span className="inline-flex h-6 max-w-full items-center gap-1 rounded-full border border-border bg-surface-sunken px-2 text-xs whitespace-nowrap text-fg-secondary lg:hidden">
        <ArrowGlyph direction="down" className="text-fg-subtle" />
        <span className="font-semibold text-fg tabular-nums">{percentText(rate)}</span>
        <span className="truncate">
          <span className="sr-only">of {from} </span>to {to.toLowerCase()}
        </span>
      </span>
      {/* in a row (lg+) */}
      <span className="hidden flex-col items-center gap-0.5 text-xs lg:flex">
        <span className="font-semibold text-fg tabular-nums">{percentText(rate)}</span>
        <span className="sr-only">
          of {from} to {to.toLowerCase()}
        </span>
        <ArrowGlyph direction="right" className="h-4 w-4 text-fg-subtle" />
      </span>
    </div>
  );
}

/**
 * Connected stages, left to right from `lg` and stacked below it. Each bar is
 * proportional to the largest stage; a conversion rate sits between each pair
 * (current ÷ previous, "—" when the previous stage is 0 or unknown). A stage
 * with `onSelect` is a real button.
 */
export function FunnelChart({
  stages,
  loading = false,
}: {
  stages: FunnelStage[];
  loading?: boolean;
}): ReactElement {
  const peak = stages.reduce(
    (max, stage) => (stage.value !== null && stage.value > max ? stage.value : max),
    0,
  );
  const columns = stages.map(() => 'minmax(0,1fr)').join(' auto ');

  return (
    <section
      aria-label="Funnel"
      aria-busy={loading || undefined}
      className="min-w-0 rounded-lg border border-border bg-surface p-1.5"
    >
      <div className="flex flex-col lg:grid lg:items-stretch" style={{ gridTemplateColumns: columns }}>
        {stages.map((stage, index) => {
          const previous = index > 0 ? stages[index - 1] : undefined;
          const rate =
            previous === undefined ||
            previous.value === null ||
            previous.value <= 0 ||
            stage.value === null
              ? null
              : stage.value / previous.value;
          const interactive = stage.onSelect !== undefined && !loading;

          return (
            <Fragment key={stage.key}>
              {previous !== undefined && (
                <FunnelConnector
                  rate={rate}
                  from={previous.label.toLowerCase()}
                  to={stage.label}
                  loading={loading}
                />
              )}
              {interactive ? (
                <button
                  type="button"
                  onClick={stage.onSelect}
                  className="group block w-full min-w-0 rounded-md px-3 py-2.5 text-left transition-colors hover:bg-surface-hover active:bg-surface-active"
                >
                  <FunnelStageBody stage={stage} peak={peak} loading={false} interactive />
                </button>
              ) : (
                <div className="min-w-0 px-3 py-2.5">
                  <FunnelStageBody
                    stage={stage}
                    peak={peak}
                    loading={loading}
                    interactive={false}
                  />
                </div>
              )}
            </Fragment>
          );
        })}
      </div>
    </section>
  );
}

// ── BarList ──────────────────────────────────────────────────────────────────

export interface BarListRow {
  key: string;
  label: string;
  value: number;
  hint?: string;
  onSelect?: () => void;
}

const BAR_LIST_MIN_BAR_PCT = 1.5;
const BAR_LIST_SKELETON_ROWS = 5;

/** Label line (label · hint · count · share) over a thin proportional bar. */
const ROW_GRID = 'grid min-w-0 grid-cols-[minmax(0,1fr)_auto_auto] items-baseline gap-x-3 gap-y-1';

function BarListRowBody({
  row,
  peak,
  denominator,
}: {
  row: BarListRow;
  peak: number;
  denominator: number;
}) {
  const width = barWidth(row.value, peak, BAR_LIST_MIN_BAR_PCT);
  const share = denominator > 0 ? row.value / denominator : null;
  const hasHint = row.hint !== undefined && row.hint !== '';
  return (
    <>
      <span className="flex min-w-0 items-baseline gap-1.5">
        <span className="truncate text-fg" title={row.label}>
          {row.label}
        </span>
        {hasHint && (
          <span className="truncate text-xs text-fg-muted max-[360px]:hidden" title={row.hint}>
            {row.hint}
          </span>
        )}
      </span>
      <span className="min-w-10 text-right font-medium text-fg tabular-nums">
        {countText(row.value)}
      </span>
      <span className="w-10 text-right text-xs text-fg-muted tabular-nums">
        <span className="sr-only">, </span>
        {percentText(share)}
        <span className="sr-only"> of total</span>
      </span>
      <span aria-hidden="true" className="col-span-3 block h-1.5 overflow-hidden rounded-xs bg-surface-sunken">
        <span
          className="block h-full rounded-xs bg-chart-1 transition-[width] duration-300 motion-reduce:transition-none"
          style={{ width: `${width}%` }}
        />
      </span>
    </>
  );
}

/**
 * A titled, ranked list: label and figures over a proportional bar, count, share
 * of total. Rows render in the order given (rank them before passing). `total`
 * is the share denominator and the header figure; when absent or `null`, the sum
 * of every row (not just the visible ones) is used for shares and the header
 * shows that sum (or "—" for an explicit `null`). Bars scale to the largest row.
 */
export function BarList({
  title,
  total,
  rows,
  loading = false,
  emptyText,
  footer,
  maxRows,
}: {
  title: string;
  total?: number | null;
  rows: BarListRow[];
  loading?: boolean;
  emptyText: string;
  footer?: ReactNode;
  maxRows?: number;
}): ReactElement {
  const titleId = useId();
  const sum = rows.reduce((acc, row) => acc + (Number.isFinite(row.value) ? row.value : 0), 0);
  const denominator = total !== undefined && total !== null && total > 0 ? total : sum;
  const headerTotal = total === undefined ? sum : total;
  const visible = maxRows !== undefined && maxRows >= 0 ? rows.slice(0, maxRows) : rows;
  const hidden = rows.length - visible.length;
  const peak = visible.reduce((max, row) => (row.value > max ? row.value : max), 0);
  const showFooter = !loading && (hidden > 0 || footer !== undefined);

  return (
    <section
      aria-labelledby={titleId}
      aria-busy={loading || undefined}
      className="flex min-w-0 flex-col rounded-lg border border-border bg-surface"
    >
      <div className="flex items-baseline justify-between gap-3 border-b border-hairline px-4 py-2.5">
        <h2 id={titleId} className="min-w-0 truncate text-sm font-semibold text-fg">
          {title}
        </h2>
        {loading ? (
          <Skeleton className="h-4 w-12" />
        ) : (
          <span className="shrink-0 text-sm font-semibold text-fg-secondary tabular-nums">
            <span className="sr-only">Total </span>
            {countText(headerTotal)}
          </span>
        )}
      </div>

      <div className="flex-1 px-2 py-1.5">
        {loading ? (
          <ul aria-hidden="true" className="flex flex-col">
            {Array.from({ length: BAR_LIST_SKELETON_ROWS }, (_, index) => (
              <li key={index} className="flex flex-col gap-1.5 px-2 py-2">
                <span className="flex items-center gap-3">
                  <Skeleton className="h-4 flex-1" />
                  <Skeleton className="h-4 w-10" />
                  <Skeleton className="h-3 w-8" />
                </span>
                <Skeleton className="h-1.5 w-full" />
              </li>
            ))}
          </ul>
        ) : visible.length === 0 ? (
          <p className="px-2 py-6 text-center text-sm text-fg-muted">{emptyText}</p>
        ) : (
          <ul className="flex flex-col">
            {visible.map((row) => (
              <li key={row.key}>
                {row.onSelect ? (
                  <button
                    type="button"
                    onClick={row.onSelect}
                    className={cx(
                      ROW_GRID,
                      'w-full rounded-md px-2 py-2 text-left text-sm transition-colors hover:bg-surface-hover active:bg-surface-active max-sm:py-2.5',
                    )}
                  >
                    <BarListRowBody row={row} peak={peak} denominator={denominator} />
                  </button>
                ) : (
                  <div className={cx(ROW_GRID, 'px-2 py-2 text-sm')}>
                    <BarListRowBody row={row} peak={peak} denominator={denominator} />
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      {showFooter && (
        <div className="flex min-h-10 flex-wrap items-center justify-between gap-x-3 gap-y-1 border-t border-hairline px-4 py-2 text-xs">
          <span className="text-fg-muted tabular-nums">
            {hidden > 0 ? `+${COUNT_FORMAT.format(hidden)} more` : null}
          </span>
          {footer !== undefined && <span className="shrink-0">{footer}</span>}
        </div>
      )}
    </section>
  );
}

// ── ActiveFilterChips ────────────────────────────────────────────────────────

/** Removable chips for every non-default filter, plus "Reset". Renders nothing when empty. */
export function ActiveFilterChips({
  chips,
  onClearAll,
}: {
  chips: { key: string; label: string; onRemove: () => void }[];
  onClearAll: () => void;
}): ReactElement | null {
  if (chips.length === 0) return null;

  return (
    <div className="flex min-w-0 flex-wrap items-center gap-1.5" role="group" aria-label="Active filters">
      {chips.map((chip) => (
        <span
          key={chip.key}
          className="inline-flex h-7 max-w-full min-w-0 items-center gap-0.5 rounded-full border border-border bg-surface-sunken pr-0.5 pl-2.5 text-xs text-fg-secondary max-sm:h-9"
        >
          <span className="truncate" title={chip.label}>
            {chip.label}
          </span>
          <Tooltip label="Remove" describes={false}>
            <button
              type="button"
              onClick={chip.onRemove}
              aria-label={`Remove ${chip.label}`}
              className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-fg-muted transition-colors hover:bg-surface-hover hover:text-fg max-sm:h-8 max-sm:w-8"
            >
              <CloseIcon className="h-3 w-3 shrink-0" />
            </button>
          </Tooltip>
        </span>
      ))}
      <Button variant="ghost" size="sm" onClick={onClearAll}>
        Reset
      </Button>
    </div>
  );
}

// ── UpdatedFact ──────────────────────────────────────────────────────────────

const TIME_FORMAT = new Intl.DateTimeFormat(undefined, { hour: '2-digit', minute: '2-digit' });
const DATE_TIME_FORMAT = new Intl.DateTimeFormat(undefined, {
  day: 'numeric',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
});

function updatedText(iso: string): string | null {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  const now = new Date();
  const sameDay =
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate();
  return (sameDay ? TIME_FORMAT : DATE_TIME_FORMAT).format(date);
}

/** "Updated 10:42" and a refresh icon button. The icon spins while a refetch is in flight. */
export function UpdatedFact({
  fetchedAt,
  refreshing,
  onRefresh,
}: {
  fetchedAt: string | null;
  refreshing: boolean;
  onRefresh: () => void;
}): ReactElement {
  const text = fetchedAt === null ? null : updatedText(fetchedAt);

  return (
    <div className="inline-flex shrink-0 items-center gap-1">
      <span className="text-xs whitespace-nowrap text-fg-muted" aria-live="polite">
        {refreshing ? (
          'Updating…'
        ) : text !== null && fetchedAt !== null ? (
          <>
            Updated{' '}
            <time dateTime={fetchedAt} className="tabular-nums">
              {text}
            </time>
          </>
        ) : null}
      </span>
      <Button
        variant="ghost"
        size="sm"
        iconOnly
        aria-label="Refresh"
        tooltip="Refresh"
        onClick={onRefresh}
        aria-busy={refreshing || undefined}
        leadingIcon={
          <RefreshIcon
            className={refreshing ? 'animate-spin motion-reduce:animate-none' : undefined}
          />
        }
      />
    </div>
  );
}
